import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccountService } from '../../../../Services/Accounts/account.service';
import { FirmService } from '../../../../Services/firm.service';
import { CountryService } from '../../../../Services/country.service';
import { StatesService } from '../../../../Services/states.service';
import { CitiesService } from '../../../../Services/cities.service';
import { NotificationService } from '../../../../Services/notification.service';
import { AuthService } from '../../../../Services/auth.service';
import { HttpParams } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-add-new-account',
  standalone: true,
  imports: [FormsModule, NgFor],
  templateUrl: './add-new-account.component.html',
  styleUrl: './add-new-account.component.css'
})
export class AddNewAccountComponent implements OnInit, OnDestroy {

  formData: any = {
    acc_user_acc: '',
    acc_firm_id: null,
    selectedAccountType: 'Bank Account',
    acc_cash_balance_date: new Date().toISOString().split('T')[0],
    acc_cash_balance: null,
    acc_cash_balance_crdr: 'DR',
    acc_description: '',      // for Account Description
    acc_other_info: '',       // for Bank Other Info
    acc_address: '',
    country_id: '',
    state_id: '',
    city_id: '',
    acc_pincode: '',
    acc_pan_it_no: '',
    acc_sales_tax_no: '',
    acc_cst_no: '',
    acc_banc_acc_no: '',
    acc_branch_name: '',
    acc_bsr_code: '',
    acc_ifs_code: '',
  };

  firms: any[] = [];
  countries: any[] = [];
  states: any[] = [];
  cities: any[] = [];
  accountTypeMappings: any = {};

  staticAccountTypes: string[] = ['Advance Money', 'Bank Account', 'Bank OD Acc', 'Branch/Divisions', 'Capital Account', 'Card Payment', 'Cash in Hand', 'CGST', 'Courier Charges', 'Current Assets', 'Current Liabilities', 'Deposits (Asset)', 'Direct Expenses', 'Direct Incomes', 'Discount Paid', 'Discount Rec', 'Duties & Taxes', 'Employee & Staff', 'Fixed Assets', 'GST Payable', 'Hallmarking Chrg', 'IGST', 'Indirect Expenses', 'Indirect Incomes', 'Interest Paid', 'Interest Rec', 'Investments', 'Labour Charges', 'Loans', 'Loans & Advances', 'Making Charges', 'Metal Rate Cut Pnl', 'Misc Expenses', 'MLJ', 'Online Payment', 'Other Charges', 'Paytm POS', 'Phone Pe', 'Processing Amount', 'Profit & Loss Acc', 'Provisions', 'Purchase Accounts', 'Purchase Gold', 'Purchase RAW Gold', 'Purchase RAW Metal', 'Purchase RAW Silver', 'Purchase Silver', 'Purchase Stone', 'RAW Alloy', 'RAW Gold', 'RAW Metal', 'RAW Silver', 'Razor Pay', 'Reserves & Surplus', 'Round Off', 'Sales Accounts', 'Sales crystal', 'Sales Gold', 'Sales RAW Gold', 'Sales RAW Metal', 'Sales RAW Silver', 'Sales Silver', 'Sales Stone', 'Saving Scheme', 'Secured Loans', 'SGST', 'Stock (Inventory)', 'Stock crystal', 'Stock Gold', 'Stock in Hand', 'Stock Raw Gold', 'Stock Raw Silver', 'Stock Silver', 'Stock Stone', 'Sundry Creditors', 'Sundry Debtors', 'Suspense Account', 'TCS', 'TDS', 'Unsecured Loans'];
  accountType: string[] = [...this.staticAccountTypes];
  crdrTypes: string[] = ['CR', 'DR'];
  private firmSubscription: Subscription | null = null;
  accountId: number | null = null;

  constructor(
    private accountService: AccountService,
    private firmService: FirmService,
    private countryService: CountryService,
    private stateService: StatesService,
    private cityService: CitiesService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private firmSelectionService: FirmSelectionService
  ) { }


  ngOnInit(): void {
    // Initial check from service
    const currentFirm = this.firmSelectionService.getselectedFirmName();
    if (currentFirm) {
      this.formData.acc_firm_id = currentFirm.id;
    } else {
      // Fallback: Initialize from localStorage immediately
      const storedFirmId = localStorage.getItem('firm_id');
      if (storedFirmId) {
        this.formData.acc_firm_id = Number(storedFirmId);
      }
    }

    this.getFirms();
    this.getCountries();
    this.getAccountDropdowns();

    // Subscribe to firm selection for restriction
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe(firm => {
      if (firm) {
        this.formData.acc_firm_id = firm.id;
        // Re-filter firms list
        this.getFirms();
      }
    });

    // Check for ID in query params for Update mode
    this.activatedRoute.queryParams.subscribe(params => {
      const id = params['id'];
      if (id) {
        this.accountId = Number(id);
        this.fetchAccountForUpdate(this.accountId);
      }
    });
  }


  ngOnDestroy(): void {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }

  getAccountDropdowns() {
    this.accountService.getDropdowns().subscribe({
      next: (res: any) => {
        const dynamicTypes = res.data?.main_accounts || [];
        this.accountTypeMappings = res.data?.mappings || {};
        // Merge static and dynamic, unique only, sort alphabetically
        this.accountType = Array.from(new Set([...this.staticAccountTypes, ...dynamicTypes])).sort();
      }
    });
  }

  fetchAccountForUpdate(id: number) {
    this.accountService.getAccount(id).subscribe({
      next: (res: any) => {
        const acc = res.data;
        this.formData = {
          ...this.formData,
          ...acc,
          selectedAccountType: acc.acc_main_acc || acc.acc_pri_grp || 'Bank Account',
          acc_cash_balance_date: acc.acc_cash_balance_date ? new Date(acc.acc_cash_balance_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          acc_pincode: acc.acc_pincode ? String(acc.acc_pincode) : '',
        };

        // Load states and cities if country/state exist
        if (this.formData.country_id) {
          this.getStates(this.formData.country_id);
          if (this.formData.state_id) {
            this.getCities(this.formData.state_id);
          }
        }
      },
      error: (err) => {
        this.notificationService.showError('Failed to fetch account details', 'Error');
      }
    });
  }


  getFirms() {
    this.firmService.getFirms().subscribe({
      next: (res: any) => {
        const allFirms = res.data || [];
        const currentFirm = this.firmSelectionService.getselectedFirmName();

        if (currentFirm) {
          // Filter to show only the logged-in/selected firm
          this.firms = allFirms.filter((f: any) => f.id == currentFirm.id);
          if (this.firms.length > 0) {
            this.formData.acc_firm_id = this.firms[0].id;
          }
        } else {
          this.firms = allFirms;
        }
      },
      error: (err) => console.error('Error loading firms', err),
    });
  }

  getCountries() {
    this.countryService.getAllCountries().subscribe({
      next: (res: any) => {
        this.countries = res.data || [];
      }
    });
  }

  getStates(countryId: any) {
    const params = new HttpParams().set('country_id', countryId.toString());
    this.stateService.getStates(params).subscribe({
      next: (res: any) => {
        this.states = res.data || [];
      }
    });
  }

  getCities(stateId: any) {
    const params = new HttpParams().set('state_id', stateId.toString());
    this.cityService.getCities(params).subscribe({
      next: (res: any) => {
        this.cities = res.data || [];
      }
    });
  }

  onCountryChange() {
    this.states = [];
    this.cities = [];
    this.formData.state_id = '';
    this.formData.city_id = '';
    if (this.formData.country_id) {
      this.getStates(this.formData.country_id);
    }
  }

  onStateChange() {
    this.cities = [];
    this.formData.city_id = '';
    if (this.formData.state_id) {
      this.getCities(this.formData.state_id);
    }
  }

  mapAccountCategory(type: string): { primary: string, group: string } {
    const t = (type || '').toLowerCase(); //  safe

    // Equity
    if (t.includes('capital') || t.includes('reserve') || t.includes('surplus') || t.includes('equity') || t.includes('drawing')) {
      return { primary: 'Equity', group: 'Equity' };
    }

    // Income
    if (t.includes('sales') || t.includes('income') || t.includes('received') || t.includes('rec')) {
      return { primary: 'Income', group: 'Income' };
    }

    // Expense
    if (t.includes('expense') || t.includes('paid') || t.includes('charges') || t.includes('labour') || t.includes('making') || t.includes('discount') || t.includes('staff')) {
      return { primary: 'Expense', group: 'Operating Expenses' };
    }

    // Assets
    if (t.includes('asset') || t.includes('bank') || t.includes('cash') || t.includes('debtor') || t.includes('stock') || t.includes('inventory') || t.includes('investment') || t.includes('advance')) {
      if (t.includes('fixed')) return { primary: 'Assets', group: 'Fixed Assets' };
      return { primary: 'Assets', group: 'Current Assets' };
    }

    // Liabilities
    if (t.includes('liability') || t.includes('loan') || t.includes('creditor') || t.includes('payable') || t.includes('duty') || t.includes('tax') || t.includes('gst')) {
      if (t.includes('long') || t.includes('secured')) return { primary: 'Liabilities', group: 'Loans & Long-Term Liabilities' };
      return { primary: 'Liabilities', group: 'Current Liabilities' };
    }

    return { primary: 'Assets', group: 'Current Assets' };
  }

  onSubmit(redirectToList: boolean = false) {

    // PRESERVE USER INPUT EXACTLY
    const accountName = (this.formData.acc_user_acc || '').trim();

    let mapping = this.mapAccountCategory(this.formData.selectedAccountType);

    // Dynamic mapping override
    if (this.accountTypeMappings[this.formData.selectedAccountType]) {
      mapping = {
        primary: this.accountTypeMappings[this.formData.selectedAccountType].primary,
        group: this.accountTypeMappings[this.formData.selectedAccountType].group
      };
    }

    const payload = {
      ...this.formData,

      // FORCE ORIGINAL CASE (IMPORTANT)
      acc_user_acc: accountName,
      acc_name: accountName,

      // DO NOT TOUCH CASE HERE
      acc_main_acc: this.formData.selectedAccountType,
      acc_pri_acc: mapping.primary,
      acc_pri_grp: mapping.group,
    };

    // Only include acc_created_by for new accounts
    if (!this.accountId) {
      payload.acc_created_by = this.authService.getUserId();
    }

    // DEBUG (REMOVE AFTER TEST)
    console.log('FINAL PAYLOAD:', payload);

    if (this.accountId) {
      // UPDATE MODE
      this.accountService.updateAccount(this.accountId, payload).subscribe({
        next: (res: any) => {
          this.notificationService.showSuccess('Account updated successfully', 'Success');
          if (redirectToList) {
            this.router.navigate(['/accounts']);
          }
        },
        error: (err: any) => {
          console.error('Error updating account:', err);
          this.notificationService.showError(err.error?.message || 'Failed to update account', 'Error');
        }
      });
    } else {
      // CREATE MODE
      this.accountService.createAccount(payload).subscribe({
        next: (res: any) => {
          this.notificationService.showSuccess('Account created successfully', 'Success');

          if (redirectToList) {
            this.router.navigate(['/accounts']);
          } else {
            this.clearForm();
          }
        },
        error: (err: any) => {
          console.error('Error creating account:', err);
          this.notificationService.showError(err.error?.message || 'Failed to create account', 'Error');
        }
      });
    }
  }

  onDelete() {
    if (this.accountId && confirm('Are you sure you want to delete this account?')) {
      this.accountService.deleteAccount(this.accountId).subscribe({
        next: () => {
          this.notificationService.showSuccess('Account deleted successfully', 'Success');
          this.router.navigate(['/accounts']);
        },
        error: (err) => {
          console.error('Error deleting account:', err);
          this.notificationService.showError(err.error?.message || 'Failed to delete account', 'Error');
        }
      });
    }
  }

  clearForm() {

    this.formData = {
      acc_user_acc: '',
      acc_firm_id: this.firms.length > 0 ? this.firms[0].id : null,
      selectedAccountType: 'Bank Account',
      acc_cash_balance_date: new Date().toISOString().split('T')[0],
      acc_cash_balance: null,
      acc_cash_balance_crdr: 'DR',
      acc_other_info: '',
      acc_address: '',
      country_id: '',
      state_id: '',
      city_id: '',
      acc_pincode: '',
      acc_pan_it_no: '',
      acc_sales_tax_no: '',
      acc_cst_no: '',
      acc_banc_acc_no: '',
      acc_branch_name: '',
      acc_bsr_code: '',
      acc_ifs_code: '',
    };
    this.states = [];
    this.cities = [];
  }
}
